import { fmtDate } from "@/lib/api";

const DAY = 86400000;
const COLORS = {
  active: "bg-[linear-gradient(90deg,#087C65,#08A881)]",
  expiring: "bg-[linear-gradient(90deg,#D97706,#F59E0B)]",
  expired: "bg-[#E5EAF0]",
};
const toMs = (d) => new Date(`${d}T00:00:00`).getTime();

function yearTicks(min, max) {
  const first = new Date(min).getFullYear() + 1;
  const last = new Date(max).getFullYear();
  const years = [];
  for (let y = first; y <= last; y++) years.push(y);
  const step = Math.ceil(years.length / 8) || 1;
  return years.filter((_, i) => i % step === 0).map((y) => ({ y, ms: new Date(y, 0, 1).getTime() }));
}

export function CoverageTimeline({ coverages }) {
  const now = new Date(new Date().toDateString()).getTime();
  const starts = coverages.map((c) => toMs(c.start_date));
  const ends = coverages.map((c) => toMs(c.expiry_date) + DAY);
  const min = Math.min(...starts, now);
  const max = Math.max(...ends, now + DAY);
  const span = Math.max(DAY, max - min);
  const pct = (ms) => ((ms - min) / span) * 100;
  const todayPct = pct(now);
  const ticks = yearTicks(min, max);

  return (
    <section className="mt-6 rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid="coverage-timeline">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold text-ww-navy">Coverage Timeline</h2>
        <div className="flex flex-wrap gap-3 text-xs text-ww-muted">
          {[["Active", "bg-ww-bright"], ["Expiring soon", "bg-amber-500"], ["Expired", "bg-[#CBD5E1]"]].map(([l, c]) => (
            <span key={l} className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${c}`} />{l}</span>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <div className="sm:grid sm:grid-cols-[30%_70%]">
          <div className="hidden sm:block" />
          <div className="relative mb-2 h-6">
            {ticks.filter((t) => Math.abs(pct(t.ms) - todayPct) > 9).map((t) => (
              <span key={t.y} className="absolute top-1 -translate-x-1/2 text-[10px] font-semibold text-ww-muted" style={{ left: `${pct(t.ms)}%` }}>{t.y}</span>
            ))}
            <span className="absolute top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-ww-navy px-2 py-0.5 text-[10px] font-bold text-white" style={{ left: `${todayPct}%` }} data-testid="timeline-today-marker">Today</span>
          </div>
        </div>

        <div className="space-y-4">
          {coverages.map((c, i) => {
            const left = pct(toMs(c.start_date));
            const width = Math.max(1.5, pct(toMs(c.expiry_date) + DAY) - left);
            const label = c.name || (c.type === "warranty" ? "Warranty" : "Guarantee");
            return (
              <div key={c.id} className="sm:grid sm:grid-cols-[30%_70%] sm:items-center" data-testid={`timeline-row-${i}`}>
                <div className="mb-1.5 pr-4 sm:mb-0">
                  <p className="truncate text-sm font-semibold text-ww-navy">{label}</p>
                  <p className="text-[11px] text-ww-muted"><span className="uppercase tracking-wide">{c.type}</span> · {fmtDate(c.start_date)} – {fmtDate(c.expiry_date)}</p>
                </div>
                <div className="relative h-9 rounded-xl bg-ww-cyan/60">
                  {ticks.map((t) => <span key={t.y} className="absolute inset-y-0 border-l border-dashed border-ww-border" style={{ left: `${pct(t.ms)}%` }} />)}
                  <span className="absolute inset-y-[-4px] z-10 w-0.5 rounded bg-ww-navy" style={{ left: `${todayPct}%` }} />
                  <div
                    className={`ww-reveal absolute inset-y-1 rounded-lg ${COLORS[c.status]}`}
                    style={{ left: `${left}%`, width: `${width}%`, animationDelay: `${i * 0.08}s` }}
                    title={`${label}: ${fmtDate(c.start_date)} – ${fmtDate(c.expiry_date)}`}
                    data-testid={`timeline-bar-${i}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="mt-6 text-xs text-ww-muted">Each period is tracked on its own, with its own reminders. The product stays active while any period is still running.</p>
    </section>
  );
}
